// src/store/modules/cdr.js
import { fetchCdr } from '@/services/cdrService'
import UsersService from '@/services/UsersService'
import CdrRecordingService from '@/services/cdrRecordingService'
import { calcRange, mapCdrRow, fmtMMSS, fmtHHMM, dedupeByUniqueId } from '@/utils/cdrUtils'

const state = () => ({
  scope: 'customer',

  // backend-supported filters
  q: '',
  datePreset: 'today',
  from: '',
  to: '',

  // optional client-side filters (kept here so UI stays dumb)
  moreFiltersOpen: false,
  src: '',
  dst: '',
  disposition: '',
  minDur: null,
  maxDur: null,
  hasRecording: false,

  // extra params (e.g., sip_user filter)
  extraParams: {},

  // paging
  page: 1,
  perPage: 25,

  // data
  rows: [],
  meta: { page: 1, per_page: 25, total: 0, total_pages: 1 },

  // ✅ cache: sip user id (TVxxxxx) -> label
  sipLabels: {},

  // ✅ cache: cdr id -> signed url (short-lived); may be null if not found
  recordingUrls: {},

  // ui state
  loading: false,
  error: ''
})

const getters = {
  scopeLabel: (s) => (s.scope === 'user' ? 'My calls' : 'Customer calls'),

  quickRangeLabel: (s) => {
    if (s.datePreset === 'custom') return `${s.from || '—'} → ${s.to || '—'}`
    return (
      {
        today: 'Today',
        yesterday: 'Yesterday',
        '7d': 'Last 7 days',
        '30d': 'Last 30 days'
      }[s.datePreset] || 'Today'
    )
  },

  uiRows: (s) => {
    let list = s.rows.slice()

    if (s.src) {
      const needle = s.src.toLowerCase()
      list = list.filter(
        (r) =>
          String(r.src || '').toLowerCase().includes(needle) ||
          String(r.srcLabel || '').toLowerCase().includes(needle) ||
          String(r.clid || '').toLowerCase().includes(needle)
      )
    }

    if (s.dst) {
      const needle = s.dst.toLowerCase()
      list = list.filter(
        (r) =>
          String(r.dst || '').toLowerCase().includes(needle) ||
          String(r.dstLabel || '').toLowerCase().includes(needle)
      )
    }

    if (s.disposition) {
      list = list.filter((r) => r.disposition === s.disposition)
    }

    if (s.minDur != null) {
      const n = Number(s.minDur)
      list = list.filter((r) => (r.durationSec || 0) >= n)
    }

    if (s.maxDur != null) {
      const n = Number(s.maxDur)
      list = list.filter((r) => (r.durationSec || 0) <= n)
    }

    if (s.hasRecording) {
      list = list.filter((r) => !!(r.hasRecording || r.recordingUrl || r.recordingFile))
    }

    return list
  },

  summary: (s, g) => {
    const list = g.uiRows
    const total = list.length
    const answered = list.filter((c) => c.disposition === 'ANSWERED').length
    const missed = list.filter((c) => c.disposition !== 'ANSWERED').length
    const talkSum = list.reduce((sum, c) => sum + (c.talkSec || 0), 0)

    return {
      total,
      answered,
      missed,
      avgTalk: total ? fmtMMSS(Math.round(talkSum / total)) : '0:00',
      totalTalk: fmtHHMM(talkSum)
    }
  },

  chips: (s, g) => {
    const chips = []

    if (s.q) chips.push({ key: 'q', label: `Search: ${s.q}`, clearType: 'SET_Q', clearValue: '' })

    if (s.datePreset !== 'today') {
      chips.push({ key: 'range', label: g.quickRangeLabel, clearType: 'SET_DATE_PRESET', clearValue: 'today' })
    }

    if (s.datePreset === 'custom' && (s.from || s.to)) {
      chips.push({
        key: 'custom',
        label: `Custom: ${s.from || '—'} → ${s.to || '—'}`,
        clearType: 'CLEAR_CUSTOM_RANGE',
        clearValue: null
      })
    }

    if (s.src) chips.push({ key: 'src', label: `From contains: ${s.src}`, clearType: 'SET_SRC', clearValue: '' })
    if (s.dst) chips.push({ key: 'dst', label: `To contains: ${s.dst}`, clearType: 'SET_DST', clearValue: '' })
    if (s.disposition)
      chips.push({ key: 'disp', label: `Outcome: ${s.disposition}`, clearType: 'SET_DISPOSITION', clearValue: '' })
    if (s.minDur != null) chips.push({ key: 'min', label: `Min ${s.minDur}s`, clearType: 'SET_MINDUR', clearValue: null })
    if (s.maxDur != null) chips.push({ key: 'max', label: `Max ${s.maxDur}s`, clearType: 'SET_MAXDUR', clearValue: null })
    if (s.hasRecording) chips.push({ key: 'rec', label: 'Has recording', clearType: 'SET_HAS_RECORDING', clearValue: false })

    return chips
  }
}

const mutations = {
  SET_SCOPE(s, v) { s.scope = v },

  SET_Q(s, v) { s.q = v },
  SET_DATE_PRESET(s, v) { s.datePreset = v },
  SET_FROM(s, v) { s.from = v },
  SET_TO(s, v) { s.to = v },
  CLEAR_CUSTOM_RANGE(s) { s.from = ''; s.to = '' },

  SET_MORE_OPEN(s, v) { s.moreFiltersOpen = !!v },
  SET_SRC(s, v) { s.src = v },
  SET_DST(s, v) { s.dst = v },
  SET_DISPOSITION(s, v) { s.disposition = v },
  SET_MINDUR(s, v) { s.minDur = v },
  SET_MAXDUR(s, v) { s.maxDur = v },
  SET_HAS_RECORDING(s, v) { s.hasRecording = !!v },

  SET_PAGE(s, v) { s.page = Number(v || 1) },
  SET_PER_PAGE(s, v) { s.perPage = Number(v || 25) },

  SET_LOADING(s, v) { s.loading = !!v },
  SET_ERROR(s, v) { s.error = v || '' },

  SET_ROWS(s, v) { s.rows = Array.isArray(v) ? v : [] },
  SET_EXTRA_PARAMS(s, v) { s.extraParams = v || {} },
  SET_META(s, m) {
    s.meta.page = Number(m?.page || 1)
    s.meta.per_page = Number(m?.per_page || s.perPage)
    s.meta.total = Number(m?.total || 0)
    s.meta.total_pages = Number(m?.total_pages || 1)
  },

  SET_SIP_LABELS(s, patch) {
    s.sipLabels = { ...s.sipLabels, ...(patch || {}) }
  },

  // ✅ recording urls cache (can be null)
  SET_RECORDING_URL(s, { id, url }) {
    const key = String(id || '').trim()
    if (!key) return
    s.recordingUrls = { ...s.recordingUrls, [key]: url ?? null }
  },

  PATCH_ROW(s, { id, patch }) {
    const key = String(id || '').trim()
    if (!key) return
    const idx = s.rows.findIndex(r => String(r?.id) === key)
    if (idx === -1) return
    const next = s.rows.slice()
    next[idx] = { ...next[idx], ...(patch || {}) }
    s.rows = next
  }
}

function isTvSipId(v) {
  return /^TV\d+$/i.test(String(v || '').trim())
}

function pickDisplayName(payload) {
  const u = payload?.user ?? payload?.data?.user ?? payload?.data ?? payload

  const tryFields = [
    u?.display_name,
    u?.displayName,
    u?.name,
    u?.full_name,
    u?.fullName,
    u?.label,
    u?.Label,
    u?.callerid_name,
    u?.CallerIDName
  ].filter(Boolean)

  if (tryFields.length) return String(tryFields[0]).trim()

  const first = u?.first_name ?? u?.FirstName ?? u?.firstname
  const last = u?.last_name ?? u?.LastName ?? u?.lastname
  const combo = [first, last].filter(Boolean).join(' ').trim()
  if (combo) return combo

  const username = u?.username ?? u?.UserName ?? u?.sip_username ?? u?.sipUsername
  if (username) return String(username).trim()

  return ''
}

function str(v) {
  return String(v ?? '').trim()
}

function extractRecordingFilename(userfield) {
  const uf = str(userfield)
  if (!uf) return ''
  const m = uf.match(/^rec:(.+)$/i)
  return m ? str(m[1]) : ''
}

const actions = {
  init({ commit }, { scope = 'customer' } = {}) {
    commit('SET_SCOPE', scope)
  },

  async ensureSipLabels({ state, commit }, ids = []) {
    const unique = Array.from(new Set((ids || []).map(x => String(x || '').trim()).filter(Boolean)))
      .filter(isTvSipId)

    const missing = unique.filter(id => !state.sipLabels[id])
    if (!missing.length) return state.sipLabels

    const limit = 6
    const out = {}

    for (let i = 0; i < missing.length; i += limit) {
      const chunk = missing.slice(i, i + limit)

      const results = await Promise.allSettled(
        chunk.map(async (sipId) => {
          const res = await UsersService.getSipUser(sipId)
          const payload = res?.data ?? res
          const name = pickDisplayName(payload)
          return { sipId, name: name || sipId }
        })
      )

      for (let j = 0; j < results.length; j++) {
        const r = results[j]
        const sipId = String(chunk[j] || '').trim()
        if (!sipId) continue
        if (r.status === 'fulfilled') out[sipId] = r.value.name
        else out[sipId] = sipId
      }
    }

    commit('SET_SIP_LABELS', out)
    return { ...state.sipLabels, ...out }
  },

  hydrateLabels({ state, commit }) {
    if (!state.rows.length) return

    const map = state.sipLabels || {}
    const patched = state.rows.map((r) => {
      const src = String(r.src || '').trim()
      const dst = String(r.dst || '').trim()

      // Prefer server-enriched labels; only override with SIP name map for TV IDs without labels
      const srcLabel = r.srcLabel && r.srcLabel !== src ? r.srcLabel : (isTvSipId(src) ? (map[src] || r.srcLabel || src) : (r.srcLabel || src))
      const dstLabel = r.dstLabel && r.dstLabel !== dst ? r.dstLabel : (isTvSipId(dst) ? (map[dst] || r.dstLabel || dst) : (r.dstLabel || dst))

      return { ...r, srcLabel, dstLabel }
    })

    commit('SET_ROWS', patched)
  },

  // ✅ resolve a signed recording url for a row (lazy, on demand)
  async ensureRecordingUrl({ state, commit }, rowOrId) {
    const id = String(rowOrId?.id ?? rowOrId ?? '').trim()
    if (!id) return null

    const existingRow = state.rows.find(r => String(r?.id) === id)

    // already on row?
    if (existingRow?.recordingUrl) return existingRow.recordingUrl

    // ✅ cached? (including cached null)
    if (Object.prototype.hasOwnProperty.call(state.recordingUrls, id)) {
      const url = state.recordingUrls[id] ?? null
      if (url) {
        commit('PATCH_ROW', { id, patch: { recordingUrl: url, hasRecording: true } })
      }
      return url
    }

    const userfield = existingRow?.userfield ?? existingRow?.userField ?? ''
    const filename = extractRecordingFilename(userfield) || str(existingRow?.recordingFile)

    const customerId = str(existingRow?.accountcode ?? existingRow?.accountCode)

    // no recording pointer? cache null & bail
    if (!filename || !customerId) {
      commit('SET_RECORDING_URL', { id, url: null })
      return null
    }

    try {
      const res = await CdrRecordingService.getRecording({ customer_id: customerId, filename })
      const payload = res?.data ?? res
      const url = payload?.url || payload?.data?.url || null

      commit('SET_RECORDING_URL', { id, url })

      if (url) {
        commit('PATCH_ROW', {
          id,
          patch: {
            recordingUrl: url,
            hasRecording: true,
            recordingFile: filename,
            accountcode: customerId,
            accountCode: customerId // ✅ safety: don’t let mixed casing break UI
          }
        })
      }

      return url
    } catch (e) {
      commit('SET_RECORDING_URL', { id, url: null })
      return null
    }
  },

  async fetch({ state, commit, dispatch }, { resetPage = false } = {}) {
    if (resetPage) commit('SET_PAGE', 1)

    commit('SET_LOADING', true)
    commit('SET_ERROR', '')

    try {
      const range = calcRange(state.datePreset, state.from, state.to)

      const res = await fetchCdr({
        scope: state.scope,
        page: state.page,
        per_page: state.perPage,
        q: state.q || undefined,
        date_from: range.date_from || undefined,
        date_to: range.date_to || undefined,
        ...(state.extraParams || {})
      })

      const data = Array.isArray(res?.data) ? res.data : []
      const meta = res?.meta || { page: 1, per_page: state.perPage, total: 0, total_pages: 1 }

      const deduped = dedupeByUniqueId(data)
      const mapped = deduped.map(mapCdrRow)

      const normalized = deduped.map((raw, i) => {
        const m = mapped[i] || {}
        const accountcode = str(raw?.accountcode ?? m?.accountcode ?? m?.accountCode)
        const userfield = raw?.userfield ?? m?.userfield ?? m?.userField ?? ''
        const recordingFile = extractRecordingFilename(userfield) || str(m?.recordingFile)

        const hasRecording = !!(recordingFile && accountcode)

        return {
          ...m,
          accountcode,
          accountCode: accountcode, // ✅ keep both so downstream code never breaks
          userfield,
          recordingFile,
          hasRecording
        }
      })

      commit('SET_ROWS', normalized)
      commit('SET_META', meta)
      commit('SET_PAGE', meta.page)
      commit('SET_PER_PAGE', meta.per_page)

      const tvIds = []
      for (const r of normalized) {
        if (isTvSipId(r.src)) tvIds.push(r.src)
        if (isTvSipId(r.dst)) tvIds.push(r.dst)
      }

      await dispatch('ensureSipLabels', tvIds)
      dispatch('hydrateLabels')
    } catch (e) {
      commit('SET_ROWS', [])
      commit('SET_META', { page: 1, per_page: state.perPage, total: 0, total_pages: 1 })
      commit('SET_ERROR', e?.response?.data?.message || e?.message || 'Failed to load CDR')
    } finally {
      commit('SET_LOADING', false)
    }
  },

  goPage({ commit, dispatch, state }, p) {
    const total = state.meta.total_pages || 1
    const next = Math.max(1, Math.min(total, Number(p || 1)))
    commit('SET_PAGE', next)
    return dispatch('fetch')
  },

  setPerPage({ commit, dispatch }, n) {
    commit('SET_PER_PAGE', n)
    commit('SET_PAGE', 1)
    return dispatch('fetch')
  },

  setDatePreset({ commit, dispatch }, v) {
    commit('SET_DATE_PRESET', v)
    commit('SET_PAGE', 1)
    return dispatch('fetch')
  },

  setQuery({ commit, dispatch }, v) {
    commit('SET_Q', v)
    commit('SET_PAGE', 1)
    return dispatch('fetch')
  },

  setCustomRange({ commit, dispatch }, { from, to }) {
    commit('SET_FROM', from || '')
    commit('SET_TO', to || '')
    commit('SET_PAGE', 1)
    return dispatch('fetch')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
